/**
 * Import d'un export **GNOME Passwords and Keys** (Seahorse, trousseau
 * GNOME Keyring / Secret Service) au format JSON : celui que lit Bitwarden
 * (« GNOME Passwords and Keys/Seahorse (json) »), produit par les scripts
 * d'export du trousseau. Chrome, Chromium, GNOME Web (Epiphany) et le reste du
 * bureau Linux y rangent leurs mots de passe : leurs utilisateurs doivent
 * pouvoir partir avec TOUT leur trousseau.
 *
 * Purement local, aucune E/S reseau : ce module transforme un export en
 * `ImportedEntry`. Le fichier est un objet `{ <trousseau>: [element...] }`,
 * chaque element `{ display_name, secret, attributes: { ... } }`. Le trousseau
 * devient le dossier de l'entree.
 *
 * Au-dela de Bitwarden, qui ne garde que les elements dont le nom commence par
 * `http` : un element sans adresse (phrase de passe d'une cle SSH, reseau
 * Wi-Fi, compte en ligne) est importe aussi, jamais perdu en silence ; un
 * mot de passe GNOME Web (`org.epiphany.FormPassword`) prend l'adresse et
 * l'identifiant de ses attributs ; les metadonnees techniques de Chrome
 * (elements de formulaire, compteurs, dates internes) ne deviennent pas des
 * champs, et un site « jamais enregistrer » de Chrome n'est pas une entree.
 */
import type { EntryCustomField } from '@klavox/crypto-core';
import { csvColumnField, type ImportedEntry } from './csv-import.js';
import { serverHost } from './typed-fields.js';

type Json = unknown;

function asObject(value: Json): Record<string, Json> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, Json>) : null;
}

function asString(value: Json): string {
  return typeof value === 'string' ? value.trim() : typeof value === 'number' || typeof value === 'boolean' ? String(value) : '';
}

/** Valeur brute d'un secret : jamais rognee, une espace peut en faire partie. */
function rawSecret(value: Json): string {
  return typeof value === 'string' ? value : asString(value);
}

/** Attributs portant l'identifiant, du plus precis au plus generique (Chrome, Epiphany, schema reseau de GNOME). */
const USERNAME_ATTRIBUTES = ['username_value', 'username', 'user'] as const;

/** Attributs portant l'adresse du site quand le nom affiche n'en est pas une (Chrome, Epiphany). */
const URI_ATTRIBUTES = ['origin_url', 'uri'] as const;

/**
 * Attributs repris par le lecteur, ou metadonnees internes sans valeur pour
 * l'utilisateur : schema libsecret, et ce que Chrome (`chrome_libsecret_password_schema`)
 * et Epiphany (`org.epiphany.FormPassword`) stockent pour remplir leurs
 * formulaires. Tout AUTRE attribut non vide devient un champ, jamais perdu.
 */
const IGNORED_ATTRIBUTES = new Set([
  'xdg:schema',
  ...USERNAME_ATTRIBUTES,
  ...URI_ATTRIBUTES,
  // Chrome / Chromium
  'action_url',
  'application',
  'avatar_url',
  'blacklisted_by_user',
  'date_created',
  'date_last_used',
  'date_password_modified',
  'date_synced',
  'display_name',
  'federation_url',
  'form_data',
  'generation_upload_status',
  'is_zero_click',
  'moving_blocked_for',
  'password_element',
  'password_type',
  'possible_username_pairs',
  'preferred',
  'scheme',
  'signon_realm',
  'skip_zero_click',
  'ssl_valid',
  'submit_element',
  'times_used',
  'type',
  'username_element',
  // GNOME Web (Epiphany)
  'form_password',
  'form_username',
  'id',
  'server_time_modified',
  'target_origin',
]);

function isWebAddress(value: string): boolean {
  return /^https?:\/\//iu.test(value);
}

/** Un element du trousseau -> une entree (identifiant, ou note s'il ne porte ni secret ni compte). */
function parseKeyringItem(item: Record<string, Json>, keyring: string): ImportedEntry | null {
  const displayName = asString(item['display_name']);
  const attributes = asObject(item['attributes']) ?? {};
  const password = rawSecret(item['secret']);
  // Site exclu par l'utilisateur (« Jamais pour ce site ») : Chrome le garde sans secret, ce n'est pas un compte.
  if (asString(attributes['blacklisted_by_user']) === '1' && password.length === 0) return null;

  const uri = isWebAddress(displayName)
    ? displayName
    : (URI_ATTRIBUTES.map((key) => asString(attributes[key])).find(isWebAddress) ?? '');
  const uris = uri.length > 0 ? [uri] : [];
  const username = USERNAME_ATTRIBUTES.map((key) => asString(attributes[key])).find((value) => value.length > 0) ?? '';

  const customFields: EntryCustomField[] = [];
  for (const [key, value] of Object.entries(attributes)) {
    if (IGNORED_ATTRIBUTES.has(key)) continue;
    const text = rawSecret(value);
    if (text.trim().length > 0) customFields.push(csvColumnField(key, text));
  }

  if (displayName.length === 0 && password.length === 0 && username.length === 0 && uris.length === 0 && customFields.length === 0) return null;
  // Ni secret, ni identifiant, ni adresse : c'est une note securisee (son contenu est dans ses champs).
  const isNote = password.length === 0 && username.length === 0 && uris.length === 0;
  const host = uri.length > 0 ? serverHost(uri).replace(/^www\./u, '') : '';
  return {
    type: isNote ? 'note' : 'login',
    name: (isWebAddress(displayName) ? host : displayName) || host || username || 'Sans nom',
    username,
    password,
    uris,
    notes: '',
    totpSecret: '',
    favorite: false,
    ...(customFields.length > 0 ? { customFields } : {}),
    ...(keyring.length > 0 ? { folderPath: [keyring] } : {}),
  };
}

/**
 * Un export GNOME se reconnait a un objet dont CHAQUE valeur est un tableau
 * d'elements portant un `display_name` texte, au moins un element en tout :
 * aucun autre format JSON lu par Klavox n'a cette forme (Bitwarden, Psono,
 * Enpass... portent des cles qui ne sont pas des tableaux d'elements nommes).
 */
export function isGnomeKeyringExport(data: Json): boolean {
  const root = asObject(data);
  if (root === null) return false;
  const keyrings = Object.values(root);
  return (
    keyrings.length > 0 &&
    keyrings.every((items) => Array.isArray(items) && items.every((item) => typeof asObject(item)?.['display_name'] === 'string')) &&
    keyrings.some((items) => (items as readonly Json[]).length > 0)
  );
}

/** Parse un export GNOME Passwords and Keys JSON : un element du trousseau = une entree, rangee sous son trousseau. */
export function parseGnomeKeyringExport(data: Json): ImportedEntry[] {
  const root = asObject(data);
  if (root === null) throw new Error('export GNOME : structure inattendue');
  const entries: ImportedEntry[] = [];
  for (const [keyring, items] of Object.entries(root)) {
    if (!Array.isArray(items)) continue;
    for (const raw of items) {
      const item = asObject(raw);
      const entry = item === null ? null : parseKeyringItem(item, keyring.trim());
      if (entry !== null) entries.push(entry);
    }
  }
  return entries;
}
